import React, { useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { CheckRounded, ExpandMoreRounded, ExpandLessRounded } from '@material-ui/icons'
import clsx from 'clsx'

import { useSelectors, useToggleExtra } from '../../../../store'
import { splitExtras } from '../../../../lib/featured-extras'

import { styles } from './styles'

// Once per module: makeStyles called inside a render built a new JSS sheet,
// with new class names, on every render (booking screen, 11 Oct 2026).
const useStyles = makeStyles(styles)

// The wash's three featured extras as tap-chips on the Book & pay screen,
// everything else behind "See all extras" (lib/featured-extras.js). Same
// selection rules as the Extras step (useToggleExtra); a tap says whether the
// chip was a featured one (`featured` on the extra events, ANALYTICS.md).
const EXTRA_GROUPS = new Set(['popular', 'extra', 'detailing'])

export const ExtrasChips = React.memo(() => {
  const classes = useStyles()
  const { baseService, prices, services, servicesAvailability } = useSelectors()
  const toggleExtra = useToggleExtra()
  const [showAll, setShowAll] = useState(false)

  const all = useMemo(
    () => prices.ids.map(id => prices.byId[id]).filter(s => s && EXTRA_GROUPS.has(s.group) && servicesAvailability[s.id]),
    [prices, servicesAvailability]
  )
  const { featured, top, rest } = useMemo(() => splitExtras(all, baseService, services), [all, baseService, services])

  if (all.length === 0) return null

  const chip = s => {
    const on = !!services[s.id]
    return (
      <button type="button" key={s.id} className={clsx(classes.chip, on && classes.chipOn)} onClick={() => toggleExtra(s.id, featured.includes(s))} aria-pressed={on}>
        <span className={clsx(classes.tick, on && classes.tickOn)}>{on && <CheckRounded style={{ fontSize: 14 }} />}</span>
        <span className={classes.chipName}>{s.name}</span>
        <span className={classes.chipPrice}>{s.customerPrice || s.price?.format?.()}</span>
      </button>
    )
  }

  return (
    <div className={classes.root} data-testid="extras-chips">
      <div className={classes.chips}>{top.map(chip)}</div>
      {rest.length > 0 && (
        <button type="button" className={classes.moreLink} onClick={() => setShowAll(v => !v)}>
          {showAll ? <ExpandLessRounded fontSize="small" /> : <ExpandMoreRounded fontSize="small" />}
          {showAll ? 'Fewer extras' : `See all extras (${rest.length} more)`}
        </button>
      )}
      {showAll && <div className={classes.chips}>{rest.map(chip)}</div>}
    </div>
  )
})
